/**
 * ActivityFeed Component
 * Displays real-time activity notifications for collaboration
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Music,
  Trash2,
  Move,
  Volume2,
  Sliders,
  Play,
  Pause,
  Square,
  Clock,
  Zap,
  Edit3,
  Copy,
  Layers,
  Activity,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';

// Activity type icons and labels
const ACTIVITY_CONFIG = {
  track_add: { icon: Music, label: 'added a track', color: 'emerald' },
  track_delete: { icon: Trash2, label: 'deleted a track', color: 'red' },
  track_update: { icon: Edit3, label: 'updated a track', color: 'blue' },
  track_move: { icon: Move, label: 'moved a track', color: 'amber' },
  clip_add: { icon: Layers, label: 'added a clip', color: 'violet' },
  clip_delete: { icon: Trash2, label: 'deleted a clip', color: 'red' },
  clip_move: { icon: Move, label: 'moved a clip', color: 'amber' },
  clip_resize: { icon: Edit3, label: 'resized a clip', color: 'blue' },
  plugin_add: { icon: Zap, label: 'added a plugin', color: 'cyan' },
  plugin_delete: { icon: Trash2, label: 'deleted a plugin', color: 'red' },
  plugin_param_change: { icon: Sliders, label: 'adjusted plugin', color: 'purple' },
  bpm_change: { icon: Clock, label: 'changed BPM', color: 'orange' },
  time_signature_change: { icon: Clock, label: 'changed time signature', color: 'cyan' },
  master_volume_change: { icon: Volume2, label: 'changed master volume', color: 'pink' },
  transport_play: { icon: Play, label: 'started playback', color: 'emerald' },
  transport_pause: { icon: Pause, label: 'paused playback', color: 'amber' },
  transport_stop: { icon: Square, label: 'stopped playback', color: 'red' },
  user_joined: { icon: Activity, label: 'joined the session', color: 'emerald' },
  user_left: { icon: Activity, label: 'left the session', color: 'zinc' },
  conflict: { icon: Zap, label: 'conflict resolved', color: 'amber' },
};

// Get relative time string
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);
  
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Single activity item
const ActivityItem = memo(({ activity, isNew }) => {
  const config = ACTIVITY_CONFIG[activity.type] || {
    icon: Activity,
    label: activity.type.replace(/_/g, ' '),
    color: 'zinc',
  };
  
  const Icon = config.icon;
  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    red: 'bg-red-500/20 text-red-400',
    blue: 'bg-blue-500/20 text-blue-400',
    amber: 'bg-amber-500/20 text-amber-400',
    violet: 'bg-violet-500/20 text-violet-400',
    cyan: 'bg-cyan-500/20 text-cyan-400',
    purple: 'bg-purple-500/20 text-purple-400',
    orange: 'bg-orange-500/20 text-orange-400',
    pink: 'bg-pink-500/20 text-pink-400',
    zinc: 'bg-zinc-500/20 text-zinc-400',
  };
  
  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: -20, scale: 0.95 } : false}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="flex items-start gap-3 px-3 py-2 hover:bg-zinc-800/30 rounded-lg transition-colors"
      data-testid="activity-item"
    >
      {/* Icon */}
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses[config.color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <span 
            className="font-medium text-white"
            style={{ color: activity.userColor }}
          >
            {activity.userName || 'Someone'}
          </span>
          <span className="text-zinc-400 ml-1">{config.label}</span>
          {activity.details && (
            <span className="text-zinc-500 ml-1">
              {activity.details}
            </span>
          )}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {getRelativeTime(activity.timestamp)}
        </div>
      </div>
    </motion.div>
  );
});

ActivityItem.displayName = 'ActivityItem';

// Main Activity Feed Component
export const ActivityFeed = memo(({
  activities = [],
  maxItems = 50,
  isCollapsed = false,
  onToggleCollapse,
  onClear,
}) => {
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const feedRef = useRef(null);
  const collapsed = isCollapsed !== undefined ? isCollapsed : localCollapsed;
  
  // Auto-scroll to bottom on new activity
  useEffect(() => {
    if (feedRef.current && !collapsed) {
      feedRef.current.scrollTop = 0;
    }
  }, [activities.length, collapsed]);
  
  // Limit activities to maxItems
  const displayActivities = activities.slice(0, maxItems);
  
  const handleToggle = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    } else {
      setLocalCollapsed(!localCollapsed);
    }
  };
  
  return (
    <div className="border-t border-zinc-800" data-testid="activity-feed">
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full px-4 py-2 flex items-center justify-between text-sm text-zinc-300 hover:bg-zinc-800/50 transition-colors"
        data-testid="activity-feed-toggle"
      >
        <span className="flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Activity
          {activities.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-zinc-700 rounded-full">
              {activities.length}
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {activities.length > 0 && onClear && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Clear activity"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </button>
      
      {/* Activity List */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              ref={feedRef}
              className="max-h-48 overflow-y-auto px-1 py-2 space-y-1"
              data-testid="activity-feed-list"
            >
              {displayActivities.length === 0 ? (
                <div className="text-center py-6 text-zinc-500 text-sm">
                  No activity yet
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {displayActivities.map((activity, index) => (
                    <ActivityItem
                      key={activity.id || index}
                      activity={activity}
                      isNew={index === 0}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

ActivityFeed.displayName = 'ActivityFeed';

// Hook to manage activity feed state
export const useActivityFeed = (maxItems = 50) => {
  const [activities, setActivities] = useState([]);
  
  const addActivity = (activity) => {
    const newActivity = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      ...activity,
    };
    
    setActivities(prev => [newActivity, ...prev].slice(0, maxItems));
  };
  
  const clearActivities = () => {
    setActivities([]);
  };
  
  return {
    activities,
    addActivity,
    clearActivities,
  };
};

export default ActivityFeed;
